import { useCallback, useEffect, useState } from "react";

import { COUNTRIES, sickCycleNote, sickPerYear } from "@core";

import type { EmployerView } from "../lib/employer";
import {
  WEEKDAY_NAMES, managers, setFenceRadius, setWorkPattern, workPattern,
  type Loaded, type Manager, type PatternDay,
} from "../lib/setup";

/**
 * The household's own settings.
 *
 * Three things, in the order they are worth: the working week, which decides
 * what public holidays and leave actually cost; the property fences, which
 * decide whether a tap is accepted; and the rules the country sets, which
 * decide nothing here but answer "where did that number come from".
 */

const RIGHTS: [keyof Manager, string][] = [
  ["canApprove", "Approve leave and overtime"],
  ["canManagePeople", "Add and manage people"],
  ["canChangeTerms", "Change what people are paid"],
  ["canManageSites", "Manage properties"],
  ["canManageManagers", "Manage other managers"],
];

export function Setup({ ctx, canApprove }: { ctx: EmployerView; canApprove: boolean }) {
  const pack = COUNTRIES[ctx.country as keyof typeof COUNTRIES] ?? COUNTRIES.OTHER;
  const [people, setPeople] = useState<Loaded<Manager[]> | null>(null);

  useEffect(() => { managers(ctx.orgId).then(setPeople); }, [ctx.orgId]);

  const money = new Intl.NumberFormat("en-ZA", {
    style: "currency", currency: ctx.currency, minimumFractionDigits: 2,
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Setup</h1>
          <p>
            {ctx.orgName} · {ctx.timezone.replace("_", " ")} ·{" "}
            {pack.name ?? ctx.country}
          </p>
        </div>
      </div>

      {/* ---------- the working week ---------- */}
      <div className="panel">
        <div className="panel-top"><div className="panel-title">Working days</div></div>
        <div className="panel-body">
          <p className="panel-note" style={{ marginTop: 0 }}>
            Which days each person is expected. This is not only a roster: a public holiday is
            paid when it falls on a day somebody <em>would ordinarily work</em> — BCEA s18(2) —
            and a holiday inside approved leave hands the leave day back under s20(8). With
            nothing recorded, Shifty treats every day as one they would work, which pays a
            holiday rather than quietly withholding it, but it is a guess.
          </p>
        </div>
        {ctx.employees.length === 0 ? (
          <div className="empty">Nobody to set days for yet.</div>
        ) : (
          ctx.employees.map((e) => (
            <PatternEditor
              key={e.id}
              employeeId={e.id}
              name={e.fullName}
              mayEdit={canApprove}
            />
          ))
        )}
      </div>

      {/* ---------- properties ---------- */}
      <div className="panel">
        <div className="panel-top"><div className="panel-title">Properties</div></div>
        {ctx.sites.length === 0 ? (
          <div className="empty">No properties yet.</div>
        ) : (
          ctx.sites.map((s) => (
            <FenceEditor key={s.id} site={s} mayEdit={canApprove} />
          ))
        )}
        <div className="panel-foot">
          <span className="panel-note">
            The fence is how far from the pin a tap is still accepted. Too tight and somebody
            standing at the gate is refused; too loose and the register stops meaning anything.
            Adding a property is still in the app, where the map is.
          </span>
        </div>
      </div>

      {/* ---------- who administers this ---------- */}
      <div className="panel">
        <div className="panel-top"><div className="panel-title">Who manages this household</div></div>
        {people === null ? (
          <div className="empty">Reading…</div>
        ) : !people.ok ? (
          <div className="notice error" style={{ margin: 22 }}>
            The list of managers could not be read, so this is not "nobody" — it is unknown.
            <div className="panel-note" style={{ marginTop: 6 }}>{people.message}</div>
          </div>
        ) : people.value.length === 0 ? (
          <div className="empty">Nobody administers this household, which should not be possible.</div>
        ) : (
          <table className="rows">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>Role</th>
                <th style={{ paddingRight: 22 }}>May</th>
              </tr>
            </thead>
            <tbody>
              {people.value.map((m) => (
                <tr key={m.userId}>
                  <td style={{ paddingLeft: 22 }}>
                    <span className="who">{m.role === "owner" ? "Owner" : "Manager"}</span>
                    <div className="panel-note">{m.userId.slice(0, 8)}…</div>
                  </td>
                  <td style={{ paddingRight: 22 }}>
                    {m.role === "owner"
                      ? "Everything — an owner's rights are not editable"
                      : RIGHTS.filter(([k]) => m[k]).map(([, label]) => label).join(", ")
                        || "Nothing beyond seeing the household"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="panel-foot">
          <span className="panel-note">
            Rights are changed in the app. A right granted by accident from a settings page is
            not a right anybody meant to grant, and these decide who can change what people are
            paid.
          </span>
        </div>
      </div>

      {/* ---------- where the defaults come from ---------- */}
      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">{pack.name ?? "Local"} rules</div>
          <span className="panel-note">What the law sets, not what you have agreed</span>
        </div>
        <table className="rows">
          <tbody>
            <Rule label="Minimum hourly wage"
              value={pack.minRate == null ? "Not set for this country" : money.format(pack.minRate)} />
            <Rule label="Ordinary hours a week" value={`${pack.weekly}`} />
            <Rule label="Ordinary hours a day" value={`${pack.daily} (${pack.dailySixDay} on a six-day week)`} />
            <Rule label="Break" value={`${pack.breakMins} min after ${pack.breakAfter} continuous hours`} />
            <Rule label="Overtime" value={`${pack.ot}×`} />
            <Rule label="Sunday" value={`${pack.sunday}×`} />
            <Rule label="Annual leave" value={`${pack.leave.annual} days`} />
            <Rule
              label="Sick leave"
              value={`${sickPerYear(pack.leave.sick, 36)} a year`}
              note={sickCycleNote(pack.leave.sick, 36) ?? undefined}
            />
            <Rule label="Family responsibility" value={`${pack.leave.family} days`} />
          </tbody>
        </table>
        <div className="panel-foot">
          <span className="panel-note">
            Reference only. What somebody is actually paid is on their own file, and Shifty warns
            when an agreement falls short of these rather than refusing to record it.
          </span>
        </div>
      </div>
    </>
  );
}

function Rule({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <tr>
      <td style={{ paddingLeft: 22 }}>
        {label}
        {note ? <div className="panel-note">{note}</div> : null}
      </td>
      <td style={{ paddingRight: 22, textAlign: "right" }} className="num">{value}</td>
    </tr>
  );
}

/**
 * Somebody's working week, as seven toggles.
 *
 * Saved as a whole rather than a day at a time: the pattern is replaced in one
 * write, so a half-applied week cannot exist.
 */
function PatternEditor({ employeeId, name, mayEdit }: {
  employeeId: string;
  name: string;
  mayEdit: boolean;
}) {
  const [days, setDays] = useState<PatternDay[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState(false);

  const load = useCallback(() => {
    workPattern(employeeId).then((r) => {
      if (!r.ok) { setFailed(r.message); return; }
      setFailed(null);
      setDays(r.value);
      setDirty(false);
    });
  }, [employeeId]);
  useEffect(load, [load]);

  function toggle(weekday: number) {
    setDays((current) => {
      const list = current ?? [];
      const on = list.some((d) => d.weekday === weekday);
      setDirty(true);
      setSaved(false);
      return on
        ? list.filter((d) => d.weekday !== weekday)
        : [...list, { weekday, startsAt: "08:00", endsAt: "16:00" }]
            .sort((a, b) => a.weekday - b.weekday);
    });
  }

  async function save() {
    if (!days) return;
    setBusy(true);
    setProblem(false);
    const ok = await setWorkPattern(employeeId, days);
    setBusy(false);
    if (!ok) { setProblem(true); return; }
    setDirty(false);
    setSaved(true);
    load();
  }

  const on = (weekday: number) => (days ?? []).some((d) => d.weekday === weekday);

  return (
    <div style={{ padding: "16px 22px", borderTop: "1px solid #f3f0f5" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <span className="who">{name}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {failed ? <span className="pill warn">Could not be read</span> : null}
          {!failed && days && days.length === 0 && !dirty ? (
            <span className="pill warn">No days set</span>
          ) : null}
          {saved ? <span className="panel-note">Saved</span> : null}
          {problem ? <span className="panel-note" style={{ color: "var(--red)" }}>Not saved</span> : null}
          {mayEdit && dirty ? (
            <button className="btn inline" type="button" disabled={busy} onClick={save}>
              {busy ? "Saving…" : "Save days"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="choices" style={{ gridAutoFlow: "column", marginTop: 10, overflowX: "auto" }}>
        {WEEKDAY_NAMES.map((label, weekday) => (
          <button
            key={label}
            type="button"
            className={`choice${on(weekday) ? " on" : ""}`}
            disabled={!mayEdit || days === null || failed !== null}
            onClick={() => toggle(weekday)}
            title={label}
            style={{ textAlign: "center", minWidth: 58 }}
          >
            {label.slice(0, 3)}
          </button>
        ))}
      </div>
    </div>
  );
}

function FenceEditor({ site, mayEdit }: {
  site: { id: string; name: string; address: string; radiusM: number };
  mayEdit: boolean;
}) {
  const [value, setValue] = useState(String(site.radiusM));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const radius = Number(value);
  // The column's own check constraint. Refused here too, so somebody typing 10
  // gets told why rather than watching a save fail.
  const valid = Number.isFinite(radius) && radius >= 20 && radius <= 500;
  const dirty = radius !== site.radiusM;

  async function save() {
    setBusy(true);
    setProblem(null);
    const ok = await setFenceRadius(site.id, radius);
    setBusy(false);
    if (!ok) {
      setProblem("Not saved — you may not have the right to manage properties here.");
      return;
    }
    setSaved(true);
  }

  return (
    <div style={{ padding: "16px 22px", borderTop: "1px solid #f3f0f5" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <span className="who">{site.name}</span>
          <div className="panel-note">{site.address || "No address recorded"}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label className="field" style={{ margin: 0 }}>
            <span>Fence radius</span>
            <input
              inputMode="numeric"
              value={value}
              onChange={(e) => { setValue(e.target.value); setSaved(false); }}
              style={{ width: 110 }}
            />
          </label>
          <span className="panel-note">metres</span>
          {mayEdit && dirty && valid ? (
            <button className="btn inline" type="button" disabled={busy} onClick={save}>
              {busy ? "Saving…" : "Save"}
            </button>
          ) : null}
          {saved && !dirty ? <span className="panel-note">Saved</span> : null}
        </div>
      </div>
      {!valid ? (
        <div className="panel-note" style={{ color: "var(--red)", marginTop: 6 }}>
          A fence has to be between 20 and 500 metres.
        </div>
      ) : null}
      {problem ? (
        <div className="panel-note" style={{ color: "var(--red)", marginTop: 6 }}>{problem}</div>
      ) : null}
    </div>
  );
}
